import type {
  ApiExtractionMilestone,
  MeteringTier,
  OAuthChecklistItem,
  Phase3State,
  PluginManifest,
  PluginRecord,
} from "./models";

const ALLOWED_PLUGIN_PERMISSIONS = new Set([
  "editor.commands",
  "analysis.read",
  "analysis.transform",
  "import.export",
  "panel.contribute",
]);

const MCP_APPROVED_SCOPES = new Set([
  "project.read",
  "project.write",
  "feedback.submit",
  "publishing.preflight",
  "plugins.read",
]);

export const TIER_LIMITS = {
  free: 20000,
  pro: 120000,
  enterprise: 500000,
} as const;

function nowIso(): string {
  return new Date().toISOString();
}

export function createDefaultPhase3State(): Phase3State {
  return {
    plugins: [] as PluginRecord[],
    tokenMetering: {
      tier: "free",
      limit: TIER_LIMITS.free,
      used: 0,
      history: [],
    },
    oauth: {
      enabled: false,
      linkedAccounts: [],
      migrationChecklist: [
        { id: "provider-inventory", label: "Inventory existing provider identities", done: false },
        { id: "linking-ui", label: "Account linking UX complete", done: false },
        { id: "token-compat", label: "Session/token compatibility validated", done: false },
        { id: "rollback", label: "Rollback path tested", done: false },
      ],
    },
    mcp: {
      approvedTools: [],
      callAudit: [],
    },
    apiExtraction: {
      milestones: [
        { id: "contracts", label: "Define shared API contracts", done: false },
        { id: "feedback-service", label: "Extract feedback ingestion service", done: false },
        { id: "publishing-service", label: "Extract publishing preflight service", done: false },
        { id: "ai-usage-service", label: "Extract AI usage/metering service", done: false },
      ],
    },
    advancedPublishing: {
      selectedTemplate: "kdp-print-pro",
      lastChecklist: [],
    },
  };
}

export function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value));
}

export function normalizePhase3State(value: unknown): Phase3State {
  const defaults = createDefaultPhase3State();
  const input =
    value && typeof value === "object"
      ? (value as Record<string, Record<string, unknown> | unknown>)
      : {};
  const tokenMetering = (input.tokenMetering ?? {}) as Record<string, unknown>;
  const oauth = (input.oauth ?? {}) as Record<string, unknown>;
  const mcp = (input.mcp ?? {}) as Record<string, unknown>;
  const apiExtraction = (input.apiExtraction ?? {}) as Record<string, unknown>;
  const advancedPublishing = (input.advancedPublishing ?? {}) as Record<string, unknown>;
  const normalizedTier =
    tokenMetering.tier === "free" || tokenMetering.tier === "pro" || tokenMetering.tier === "enterprise"
      ? tokenMetering.tier
      : defaults.tokenMetering.tier;
  return {
    plugins: Array.isArray(input.plugins) ? input.plugins : defaults.plugins,
    tokenMetering: {
      tier: normalizedTier,
      limit:
        Number(tokenMetering.limit) > 0
          ? Number(tokenMetering.limit)
          : TIER_LIMITS[normalizedTier] || defaults.tokenMetering.limit,
      used: Number(tokenMetering.used) > 0 ? Number(tokenMetering.used) : 0,
      history: Array.isArray(tokenMetering.history) ? tokenMetering.history : [],
    },
    oauth: {
      enabled: Boolean(oauth.enabled),
      linkedAccounts: Array.isArray(oauth.linkedAccounts) ? oauth.linkedAccounts : [],
      migrationChecklist: Array.isArray(oauth.migrationChecklist)
        ? oauth.migrationChecklist
        : defaults.oauth.migrationChecklist,
    },
    mcp: {
      approvedTools: Array.isArray(mcp.approvedTools) ? mcp.approvedTools : [],
      callAudit: Array.isArray(mcp.callAudit) ? mcp.callAudit : [],
    },
    apiExtraction: {
      milestones: Array.isArray(apiExtraction.milestones)
        ? apiExtraction.milestones
        : defaults.apiExtraction.milestones,
    },
    advancedPublishing: {
      selectedTemplate:
        typeof advancedPublishing.selectedTemplate === "string"
          ? advancedPublishing.selectedTemplate
          : defaults.advancedPublishing.selectedTemplate,
      lastChecklist: Array.isArray(advancedPublishing.lastChecklist)
        ? advancedPublishing.lastChecklist
        : [],
    },
  };
}

export function validatePluginManifest(manifest: unknown): { valid: boolean; problems: string[] } {
  const problems: string[] = [];
  if (!manifest || typeof manifest !== "object") {
    return { valid: false, problems: ["Manifest must be an object."] };
  }
  const parsed = manifest as Record<string, unknown>;

  const requiredStringFields = ["id", "name", "version", "author", "compatibilityRange"];
  for (const field of requiredStringFields) {
    if (typeof parsed[field] !== "string" || !parsed[field].trim()) {
      problems.push(`Missing required string field: ${field}`);
    }
  }

  if (!Array.isArray(parsed.entrypoints) || parsed.entrypoints.length === 0) {
    problems.push("entrypoints must be a non-empty array.");
  }
  if (!Array.isArray(parsed.permissions)) {
    problems.push("permissions must be an array.");
  } else {
    for (const permission of parsed.permissions) {
      if (!ALLOWED_PLUGIN_PERMISSIONS.has(permission)) {
        problems.push(`Permission not allowed in phase 3 baseline: ${permission}`);
      }
    }
  }

  return { valid: problems.length === 0, problems };
}

export function registerPlugin(
  state: Phase3State,
  manifest: PluginManifest,
  signature: string | null
): {
  ok: boolean;
  validation: { valid: boolean; problems: string[] };
  plugin?: PluginRecord;
  state: Phase3State;
} {
  const next = normalizePhase3State(state);
  const validation = validatePluginManifest(manifest);
  if (!validation.valid) {
    return { ok: false, validation, state: next };
  }

  const existing = next.plugins.find((plugin) => plugin.id === manifest.id);
  const pluginRecord: PluginRecord = {
    id: manifest.id,
    name: manifest.name,
    version: manifest.version,
    author: manifest.author,
    permissions: manifest.permissions,
    entrypoints: manifest.entrypoints,
    compatibilityRange: manifest.compatibilityRange,
    signed: Boolean(signature),
    signature: signature || null,
    installedAt: nowIso(),
    mode: signature ? "privileged-ready" : "sandbox-only",
  };

  if (existing) {
    Object.assign(existing, pluginRecord);
  } else {
    next.plugins.push(pluginRecord);
  }

  return { ok: true, validation, plugin: pluginRecord, state: next };
}

function estimateTokensFromPrompt(prompt: string): number {
  const chars = String(prompt || "").length;
  return Math.max(1, Math.ceil(chars / 4));
}

export function consumeManagedTokens(
  state: Phase3State,
  prompt: string,
  tier?: keyof typeof TIER_LIMITS
): {
  state: Phase3State;
  result: {
    tier: MeteringTier;
    cost: number;
    allowed: boolean;
    used: number;
    remaining: number;
    limit: number;
  };
} {
  const next = normalizePhase3State(state);
  const chosenTier: keyof typeof TIER_LIMITS =
    tier && TIER_LIMITS[tier]
      ? tier
      : ((next.tokenMetering.tier as keyof typeof TIER_LIMITS) || "free");
  const limit = TIER_LIMITS[chosenTier];
  const cost = estimateTokensFromPrompt(prompt);
  const remaining = Math.max(0, limit - next.tokenMetering.used);
  const allowed = cost <= remaining;

  if (allowed) {
    next.tokenMetering.used += cost;
  }
  next.tokenMetering.tier = chosenTier;
  next.tokenMetering.limit = limit;
  next.tokenMetering.history.push({
    at: nowIso(),
    cost,
    allowed,
    remainingAfter: allowed ? Math.max(0, limit - next.tokenMetering.used) : remaining,
  });

  return {
    state: next,
    result: {
      tier: chosenTier,
      cost,
      allowed,
      used: next.tokenMetering.used,
      remaining: Math.max(0, limit - next.tokenMetering.used),
      limit,
    },
  };
}

export function toggleOAuthChecklistItem(state: Phase3State, itemId: string): Phase3State {
  const next = normalizePhase3State(state);
  next.oauth.migrationChecklist = next.oauth.migrationChecklist.map((item: OAuthChecklistItem) =>
    item.id === itemId ? { ...item, done: !item.done } : item
  );
  return next;
}

export function linkOAuthAccount(state: Phase3State, provider: string, providerUserId: string): Phase3State {
  const next = normalizePhase3State(state);
  next.oauth.enabled = true;
  next.oauth.linkedAccounts.push({
    provider: String(provider || "unknown"),
    providerUserId: String(providerUserId || "unknown"),
    linkedAt: nowIso(),
  });
  return next;
}

export function evaluateMcpToolCall(
  state: Phase3State,
  toolName: string,
  scope: string
): { approved: boolean; reason: string; state: Phase3State } {
  const next = normalizePhase3State(state);
  const approved = MCP_APPROVED_SCOPES.has(scope);
  next.mcp.callAudit.push({
    at: nowIso(),
    toolName,
    scope,
    approved,
  });
  if (approved && !next.mcp.approvedTools.some((item) => item.toolName === toolName && item.scope === scope)) {
    next.mcp.approvedTools.push({ toolName, scope, approvedAt: nowIso() });
  }
  return {
    approved,
    reason: approved ? "Scope is approved for phase 3 baseline." : "Scope is not approved yet.",
    state: next,
  };
}

export function buildPublishingTemplateChecklist(templateId: string) {
  const templates: Record<string, string[]> = {
    "kdp-print-pro": [
      "Trim size matches KDP target profile",
      "Margins and gutter validated by estimated page count",
      "Font embedding and print-safe image DPI validated",
      "Front/back matter checklist complete",
    ],
    "kdp-ebook-pro": [
      "EPUB nav and manifest checks pass",
      "Heading hierarchy and chapter boundaries validated",
      "Image dimensions and reflow behavior reviewed",
      "Metadata and rights fields complete",
    ],
    "ingramspark-print-pro": [
      "IngramSpark trim/bleed rules validated",
      "Embedded fonts and print quality checks pass",
      "Cover/interior specs selected",
      "Distribution metadata fields complete",
    ],
  };
  const selected = templates[templateId] || templates["kdp-print-pro"];
  return selected.map((label, index) => ({
    id: `${templateId}-${index + 1}`,
    label,
    done: false,
  }));
}

export function setApiExtractionMilestone(
  state: Phase3State,
  milestoneId: string,
  done: boolean
): Phase3State {
  const next = normalizePhase3State(state);
  next.apiExtraction.milestones = next.apiExtraction.milestones.map((item: ApiExtractionMilestone) =>
    item.id === milestoneId ? { ...item, done: Boolean(done) } : item
  );
  return next;
}
